import { Confidence } from '@spotify-confidence/sdk'
import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from 'react'
import { resolveConfidenceVisitorId } from '../helpers/confidenceVisitorId'
import { setABTestVariantInContext } from '../helpers/variantContext'

export interface ABTestConfig {
  clientSecret: string
  flagName: string
  defaultVariant?: string
  enabledVariants?: string[]
}

interface ABTestContextValue {
  variant: string
  setVariant: (variant: 'baseline' | 'v1' | 'v2') => void
  isLoading: boolean
}

const ABTestContext = createContext<ABTestContextValue | null>(null)

// Singleton: Store Confidence instances by client secret to reuse across mounts
const confidenceInstances = new Map<string, Confidence>()

/**
 * Get or create a Confidence instance (singleton per client secret)
 */
function getConfidenceInstance(config: ABTestConfig): Confidence {
  const cacheKey = `${config.clientSecret}`

  if (!confidenceInstances.has(cacheKey)) {
    const instance = Confidence.create({
      clientSecret: config.clientSecret,
      environment: 'client',
      region: 'eu',
      timeout: 1000,
    })

    // Set from our own resolver rather than the SDK's `visitorIdentity()`
    // tracker, which re-reads the cookie on every page load and mints a fresh
    // uuid whenever it cannot find one. On the iOS in-app browsers that drop
    // the cookie mid-session that produced a different randomisation unit per
    // page load; this keeps one id for the whole visit.
    const visitorId = resolveConfidenceVisitorId()

    if (visitorId) {
      instance.setContext({ visitor_id: visitorId })
      console.log('✓ Visitor identity initialized')
    }

    confidenceInstances.set(cacheKey, instance)
    console.log('✓ Confidence instance created and cached')
  }

  return confidenceInstances.get(cacheKey)!
}

interface ABTestProviderProps {
  children: ReactNode
  config: ABTestConfig
}

export function ABTestProvider({ children, config }: ABTestProviderProps) {
  const [variant, setVariant] = useState<string>(
    config.defaultVariant || 'baseline',
  )
  const [isLoading, setIsLoading] = useState<boolean>(true)

  // Get or create the singleton Confidence instance
  const confidence = getConfidenceInstance(config)

  useEffect(() => {
    const resolveVariant = async () => {
      try {
        // Get flag value using dot notation to access the nested variant field
        // The flag value structure is: { variant: "v1" }, so we access flag.variant
        const flagValue = await confidence.getFlag(
          `${config.flagName}.variant`,
          config.defaultVariant || 'baseline',
        )

        console.log('✓ Resolved variant:', flagValue)

        if (typeof flagValue === 'string') {
          // Validate variant against enabled variants if provided
          if (
            config.enabledVariants &&
            !config.enabledVariants.includes(flagValue)
          ) {
            console.warn(
              `Variant "${flagValue}" not in enabled variants, using default`,
            )
            const defaultVariant = config.defaultVariant || 'baseline'
            setVariant(defaultVariant)
            setABTestVariantInContext(defaultVariant)
          } else {
            setVariant(flagValue)
            setABTestVariantInContext(flagValue)
          }
        } else {
          console.warn('No valid flag value resolved, using default variant')
          const defaultVariant = config.defaultVariant || 'baseline'
          setVariant(defaultVariant)
          setABTestVariantInContext(defaultVariant)
        }
      } catch (error) {
        console.error('Error resolving A/B test variant:', error)
        const defaultVariant = config.defaultVariant || 'baseline'
        setVariant(defaultVariant)
        setABTestVariantInContext(defaultVariant)
      } finally {
        setIsLoading(false)
      }
    }

    resolveVariant()
    // Only resolve once on mount - no dependencies to prevent refetches
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <ABTestContext.Provider
      value={{
        variant,
        setVariant,
        isLoading,
      }}
    >
      {children}
    </ABTestContext.Provider>
  )
}

export function useABTest() {
  const context = useContext(ABTestContext)

  if (!context) {
    throw new Error('useABTest must be used within an ABTestProvider')
  }

  return context
}

export function useABTestVariant() {
  const { variant, setVariant, isLoading } = useABTest()
  return { variant, setVariant, isLoading }
}
